// src/MarketplacePage.js
// Owners-only Marketplace / Classifieds for Nantahala Mountain Resort
// Buy, sell, trade gear, services, and free items within the trusted owner community.
// Real-time updates, photo support via Firebase Storage, owner controls.

import React, { useState, useEffect, useCallback } from 'react';
import { db, storage, auth } from './firebase.js';
import {
  collection, addDoc, deleteDoc, doc, updateDoc,
  onSnapshot, query, orderBy
} from 'firebase/firestore';
import {
  ref as storageRef, uploadBytes, getDownloadURL, deleteObject
} from 'firebase/storage';

const CATEGORIES = ['For Sale', 'Wanted', 'Services Offered', 'Free / Giveaway'];

const CATEGORY_COLORS = {
  'For Sale': 'bg-emerald-600 text-white',
  'Wanted': 'bg-sky-600 text-white',
  'Services Offered': 'bg-amber-600 text-white',
  'Free / Giveaway': 'bg-violet-600 text-white',
};

export default function MarketplacePage({ user, isAdmin, isMobile }) {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [activeFilter, setActiveFilter] = useState('All');
  const [searchTerm, setSearchTerm] = useState('');
  const [showSold, setShowSold] = useState(false); // include sold items

  const [showNewModal, setShowNewModal] = useState(false);
  const [selectedPost, setSelectedPost] = useState(null);

  // New post form state
  const [newTitle, setNewTitle] = useState('');
  const [newDescription, setNewDescription] = useState('');
  const [newCategory, setNewCategory] = useState(CATEGORIES[0]);
  const [newPrice, setNewPrice] = useState('');
  const [newContact, setNewContact] = useState('');
  const [newPhotoFile, setNewPhotoFile] = useState(null);
  const [newPhotoPreview, setNewPhotoPreview] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Realtime listener for posts (new posts appear instantly for everyone)
  useEffect(() => {
    if (!user) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    const q = query(
      collection(db, 'marketplace_posts'),
      orderBy('createdAt', 'desc')
    );

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const list = snapshot.docs.map((d) => ({
          id: d.id,
          ...d.data(),
        }));
        setPosts(list);
        setLoading(false);
      },
      (err) => {
        console.error('Marketplace snapshot error:', err);
        setError('Failed to load marketplace listings. ' + (err.message || ''));
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [user]);

  // Filtered + searched posts
  const filteredPosts = React.useMemo(() => {
    return posts.filter((p) => {
      const matchesCategory = activeFilter === 'All' || p.category === activeFilter;
      const matchesSearch =
        !searchTerm ||
        (p.title && p.title.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (p.description && p.description.toLowerCase().includes(searchTerm.toLowerCase()));
      const matchesSold = showSold || !p.isSold;
      return matchesCategory && matchesSearch && matchesSold;
    });
  }, [posts, activeFilter, searchTerm, showSold]);

  const activeCount = filteredPosts.length;
  const totalActive = posts.filter((p) => !p.isSold).length;

  // Cleanup preview URL
  const cleanupPreview = useCallback(() => {
    if (newPhotoPreview) {
      URL.revokeObjectURL(newPhotoPreview);
      setNewPhotoPreview(null);
    }
  }, [newPhotoPreview]);

  // Handle photo selection for new post
  const handlePhotoChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    cleanupPreview();
    setNewPhotoFile(file);
    setNewPhotoPreview(URL.createObjectURL(file));
  };

  // Reset new post form
  const resetNewForm = () => {
    setNewTitle('');
    setNewDescription('');
    setNewCategory(CATEGORIES[0]);
    setNewPrice('');
    setNewContact('');
    setNewPhotoFile(null);
    cleanupPreview();
    setError(null);
  };

  // Close new modal + reset
  const closeNewModal = () => {
    setShowNewModal(false);
    resetNewForm();
  };

  // Create new marketplace post (with optional photo upload)
  const handleCreatePost = async (e) => {
    e.preventDefault();
    if (!user) {
      setError('You must be logged in to post.');
      return;
    }
    if (!newTitle.trim() || !newDescription.trim()) {
      setError('Title and description are required.');
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      let photoURL = null;
      let storagePath = null;

      if (newPhotoFile) {
        storagePath = `Marketplace/${Date.now()}_${newPhotoFile.name.replace(/\s+/g, '_')}`;
        const photoRef = storageRef(storage, storagePath);
        await uploadBytes(photoRef, newPhotoFile);
        photoURL = await getDownloadURL(photoRef);
      }

      const postData = {
        title: newTitle.trim(),
        description: newDescription.trim(),
        category: newCategory,
        price: newPrice.trim() || '',
        contact: newContact.trim() || '',
        photoURL,
        storagePath,
        postedByEmail: user.email || 'owner',
        postedByUid: user.uid,
        createdAt: new Date().toISOString(),
        isSold: false,
      };

      await addDoc(collection(db, 'marketplace_posts'), postData);

      closeNewModal();
      // Real-time listener will add it instantly
    } catch (err) {
      console.error('Failed to create marketplace post:', err);
      setError('Failed to create listing: ' + (err.message || 'Please try again.'));
    } finally {
      setIsSubmitting(false);
    }
  };

  // Toggle sold status (owner or admin only)
  const toggleSold = async (post) => {
    if (!user) return;
    const canEdit = isAdmin || post.postedByUid === user.uid;
    if (!canEdit) return;

    try {
      await updateDoc(doc(db, 'marketplace_posts', post.id), {
        isSold: !post.isSold,
      });
      // onSnapshot will refresh
      if (selectedPost && selectedPost.id === post.id) {
        setSelectedPost({ ...selectedPost, isSold: !post.isSold });
      }
    } catch (err) {
      console.error('Toggle sold failed:', err);
      alert('Could not update listing status.');
    }
  };

  // Delete a post (owner or admin). Also cleans up storage photo if present.
  const handleDelete = async (post) => {
    if (!user) return;
    const canDelete = isAdmin || post.postedByUid === user.uid;
    if (!canDelete) return;

    if (!window.confirm(`Delete "${post.title}"? This cannot be undone.`)) return;

    try {
      if (post.storagePath) {
        try {
          await deleteObject(storageRef(storage, post.storagePath));
        } catch (storageErr) {
          console.warn('Storage cleanup warning (photo may already be gone):', storageErr);
        }
      }
      await deleteDoc(doc(db, 'marketplace_posts', post.id));
      if (selectedPost && selectedPost.id === post.id) {
        setSelectedPost(null);
      }
    } catch (err) {
      console.error('Delete post failed:', err);
      alert('Failed to delete listing.');
    }
  };

  // Open detail modal
  const openDetail = (post) => {
    setSelectedPost(post);
  };

  const closeDetail = () => {
    setSelectedPost(null);
  };

  // Contact action ΓÇö mailto with prefilled subject
  const contactPoster = (post) => {
    const email = post.postedByEmail || '';
    const subject = encodeURIComponent(`Re: ${post.title} (Nantahala Marketplace)`);
    if (email) {
      window.location.href = `mailto:${email}?subject=${subject}`;
    } else {
      // Fallback: copy email or show note
      if (post.contact) {
        alert(`Contact info from poster: ${post.contact}`);
      } else {
        alert('No direct contact listed. Try replying in the Message Board.');
      }
    }
  };

  // Format a readable date
  const formatDate = (iso) => {
    if (!iso) return '';
    try {
      return new Date(iso).toLocaleDateString(undefined, {
        month: 'short',
        day: 'numeric',
        year: new Date(iso).getFullYear() === new Date().getFullYear() ? undefined : 'numeric',
      });
    } catch {
      return '';
    }
  };

  // Can the current user manage this post?
  const canManage = (post) => isAdmin || (user && post.postedByUid === user.uid);

  if (!user) {
    return (
      <div className="p-6 max-w-4xl mx-auto text-slate-200">
        <p>Please log in as an owner to access the Marketplace.</p>
      </div>
    );
  }

  return (
    <div className="p-4 max-w-6xl mx-auto text-slate-200">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-2 mb-4">
        <div>
          <h1 className="text-3xl font-semibold">Nantahala Owners Marketplace</h1>
          <p className="text-slate-400 text-sm mt-1">
            Buy, sell, trade gear, services &amp; more with your neighbors. Trusted owners only.
          </p>
        </div>
        <div className="text-sm text-emerald-400">
          {totalActive} active listing{totalActive === 1 ? '' : 's'}
        </div>
      </div>

      {/* Controls */}
      <div className="flex flex-col gap-3 mb-4">
        <div className="flex flex-wrap gap-2 items-center">
          <button
            onClick={() => setShowNewModal(true)}
            className="bg-emerald-600 hover:bg-emerald-500 active:bg-emerald-700 transition px-4 py-2 rounded-lg text-sm font-medium flex items-center gap-2"
          >
            + Post New Listing
          </button>

          <div className="ml-auto flex items-center gap-2 text-sm">
            <label className="flex items-center gap-1.5 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={showSold}
                onChange={(e) => setShowSold(e.target.checked)}
                className="accent-emerald-500"
              />
              Show sold items
            </label>
          </div>
        </div>

        {/* Search + Category filters */}
        <div className="flex flex-col sm:flex-row gap-3">
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search listings (title or description)..."
            className="flex-1 bg-slate-800 border border-slate-700 focus:border-emerald-500 rounded-lg px-4 py-2 text-sm outline-none"
          />

          <div className="flex flex-wrap gap-1.5">
            <button
              onClick={() => setActiveFilter('All')}
              className={`px-3 py-1.5 text-sm rounded-full border transition ${activeFilter === 'All'
                ? 'bg-white text-slate-900 border-white'
                : 'bg-slate-800 border-slate-700 hover:bg-slate-700'}`}
            >
              All
            </button>
            {CATEGORIES.map((cat) => (
              <button
                key={cat}
                onClick={() => setActiveFilter(cat)}
                className={`px-3 py-1.5 text-sm rounded-full border transition ${activeFilter === cat
                  ? 'bg-white text-slate-900 border-white'
                  : 'bg-slate-800 border-slate-700 hover:bg-slate-700'}`}
              >
                {cat}
              </button>
            ))}
          </div>
        </div>

        {error && (
          <div className="text-sm bg-red-900/60 border border-red-700 text-red-200 px-3 py-2 rounded">
            {error}
          </div>
        )}
      </div>

      {/* Listings grid */}
      {loading ? (
        <div className="py-12 text-center text-slate-400">Loading marketplace...</div>
      ) : filteredPosts.length === 0 ? (
        <div className="py-10 text-center border border-slate-700 rounded-2xl bg-slate-900/50">
          <div className="text-4xl mb-3">≡ƒ¢Æ</div>
          <p className="font-medium">No listings match your filters.</p>
          <p className="text-sm text-slate-400 mt-1">Be the first to post something in this category!</p>
          <button
            onClick={() => setShowNewModal(true)}
            className="mt-4 text-sm underline text-emerald-400 hover:text-emerald-300"
          >
            Post a new listing
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredPosts.map((post) => (
            <div
              key={post.id}
              onClick={() => openDetail(post)}
              className={`group bg-slate-900 border border-slate-700 rounded-2xl overflow-hidden cursor-pointer transition hover:border-emerald-600/70 ${post.isSold ? 'opacity-70' : ''}`}
            >
              <div className="relative h-44 bg-slate-800">
                {post.photoURL ? (
                  <img
                    src={post.photoURL}
                    alt={post.title}
                    className="absolute inset-0 w-full h-full object-cover"
                  />
                ) : (
                  <div className="absolute inset-0 flex items-center justify-center text-5xl text-slate-700">
                    ≡ƒôª
                  </div>
                )}
                {post.isSold && (
                  <div className="absolute top-3 right-3 px-3 py-0.5 text-xs font-bold tracking-wider bg-red-600 text-white rounded">
                    SOLD
                  </div>
                )}
                <div className={`absolute top-3 left-3 px-2.5 py-0.5 text-[10px] font-medium rounded ${CATEGORY_COLORS[post.category] || 'bg-slate-600'}`}>
                  {post.category}
                </div>
              </div>

              <div className="p-4">
                <div className="flex justify-between items-start gap-2">
                  <h3 className="font-semibold text-lg leading-tight line-clamp-2 pr-1">{post.title}</h3>
                  {post.price && (
                    <div className="shrink-0 text-right text-emerald-400 font-medium text-sm tabular-nums">
                      {post.price}
                    </div>
                  )}
                </div>

                <p className="mt-1.5 text-sm text-slate-400 line-clamp-2">{post.description}</p>

                <div className="mt-3 flex items-center justify-between text-xs text-slate-500">
                  <span>{post.postedByEmail?.split('@')[0] || 'owner'}</span>
                  <span>{formatDate(post.createdAt)}</span>
                </div>

                <div className="mt-3 text-emerald-400 text-sm font-medium group-hover:underline">
                  View details ΓåÆ
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="mt-8 text-xs text-slate-500 border-t border-slate-800 pt-4">
        Only visible to logged-in Nantahala Mountain Resort owners. Please be respectful and safe ΓÇö meet in public for exchanges.
        Listings are the responsibility of the poster.
      </div>

      {/* NEW POSTING MODAL */}
      {showNewModal && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/70 p-4" onClick={closeNewModal}>
          <div
            className="bg-slate-900 border border-slate-700 rounded-2xl w-full max-w-lg overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-5 py-4 border-b border-slate-700 flex items-center justify-between">
              <div className="font-semibold">Post a new listing</div>
              <button onClick={closeNewModal} className="text-slate-400 hover:text-white text-xl leading-none">├ù</button>
            </div>

            <form onSubmit={handleCreatePost} className="p-5 space-y-4">
              <div>
                <label className="block text-xs uppercase tracking-widest text-slate-400 mb-1">Title</label>
                <input
                  type="text"
                  value={newTitle}
                  onChange={(e) => setNewTitle(e.target.value)}
                  required
                  maxLength={80}
                  placeholder="e.g. 14ft Old Town canoe with paddles"
                  className="w-full bg-slate-800 border border-slate-700 focus:border-emerald-500 rounded-lg px-3 py-2 text-sm"
                />
              </div>

              <div>
                <label className="block text-xs uppercase tracking-widest text-slate-400 mb-1">Category</label>
                <select
                  value={newCategory}
                  onChange={(e) => setNewCategory(e.target.value)}
                  className="w-full bg-slate-800 border border-slate-700 focus:border-emerald-500 rounded-lg px-3 py-2 text-sm"
                >
                  {CATEGORIES.map((c) => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                </select>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs uppercase tracking-widest text-slate-400 mb-1">Price / Terms</label>
                  <input
                    type="text"
                    value={newPrice}
                    onChange={(e) => setNewPrice(e.target.value)}
                    placeholder="$275  or  Trade  or  Free"
                    className="w-full bg-slate-800 border border-slate-700 focus:border-emerald-500 rounded-lg px-3 py-2 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs uppercase tracking-widest text-slate-400 mb-1">Extra contact (optional)</label>
                  <input
                    type="text"
                    value={newContact}
                    onChange={(e) => setNewContact(e.target.value)}
                    placeholder="Text 828-555-0199"
                    className="w-full bg-slate-800 border border-slate-700 focus:border-emerald-500 rounded-lg px-3 py-2 text-sm"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs uppercase tracking-widest text-slate-400 mb-1">Description</label>
                <textarea
                  value={newDescription}
                  onChange={(e) => setNewDescription(e.target.value)}
                  required
                  rows={4}
                  maxLength={600}
                  placeholder="Condition, location (e.g. near the lodge), pickup details, etc."
                  className="w-full bg-slate-800 border border-slate-700 focus:border-emerald-500 rounded-lg px-3 py-2 text-sm resize-y"
                />
              </div>

              <div>
                <label className="block text-xs uppercase tracking-widest text-slate-400 mb-1">Photo (recommended)</label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handlePhotoChange}
                  className="block w-full text-sm file:mr-3 file:py-1.5 file:px-3 file:rounded file:border-0 file:text-sm file:bg-slate-700 file:text-white hover:file:bg-slate-600"
                />
                {newPhotoPreview && (
                  <div className="mt-2 relative w-full h-36 bg-black rounded-lg overflow-hidden border border-slate-700">
                    <img src={newPhotoPreview} alt="Preview" className="object-contain w-full h-full" />
                    <button
                      type="button"
                      onClick={() => { setNewPhotoFile(null); cleanupPreview(); }}
                      className="absolute top-1 right-1 text-[10px] bg-black/70 px-2 py-0.5 rounded"
                    >
                      Remove
                    </button>
                  </div>
                )}
              </div>

              {error && <div className="text-xs text-red-400">{error}</div>}

              <div className="flex gap-3 pt-2">
                <button
                  type="button"
                  onClick={closeNewModal}
                  className="flex-1 py-2 rounded-lg bg-slate-800 hover:bg-slate-700 text-sm"
                  disabled={isSubmitting}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting || !newTitle.trim() || !newDescription.trim()}
                  className="flex-1 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-500 disabled:opacity-60 text-sm font-medium"
                >
                  {isSubmitting ? 'Posting...' : 'Post to Marketplace'}
                </button>
              </div>
              <p className="text-[10px] text-center text-slate-500">Your email will be shown to other owners for contact.</p>
            </form>
          </div>
        </div>
      )}

      {/* DETAIL MODAL */}
      {selectedPost && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/80 p-4" onClick={closeDetail}>
          <div
            className="bg-slate-900 border border-slate-700 rounded-2xl w-full max-w-2xl overflow-hidden max-h-[92vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Hero image or placeholder */}
            <div className="relative w-full h-64 sm:h-80 bg-slate-800">
              {selectedPost.photoURL ? (
                <img
                  src={selectedPost.photoURL}
                  alt={selectedPost.title}
                  className="absolute inset-0 w-full h-full object-cover"
                />
              ) : (
                <div className="absolute inset-0 flex items-center justify-center text-[120px] text-slate-800 select-none">≡ƒôª</div>
              )}
              {selectedPost.isSold && (
                <div className="absolute top-4 right-4 px-4 py-1 text-sm font-bold bg-red-600 rounded">SOLD</div>
              )}
              <div className={`absolute top-4 left-4 px-3 py-1 text-xs font-semibold rounded ${CATEGORY_COLORS[selectedPost.category] || 'bg-slate-700'}`}>
                {selectedPost.category}
              </div>
            </div>

            <div className="p-5">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <h2 className="text-2xl font-semibold leading-tight">{selectedPost.title}</h2>
                  {selectedPost.price && (
                    <div className="mt-1 text-2xl text-emerald-400 font-medium tabular-nums">{selectedPost.price}</div>
                  )}
                </div>
                <button onClick={closeDetail} className="text-2xl leading-none text-slate-400 hover:text-white">├ù</button>
              </div>

              <div className="mt-4 whitespace-pre-wrap text-slate-200 text-[15px] leading-relaxed">
                {selectedPost.description}
              </div>

              <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1 text-sm">
                <div className="text-slate-400">Posted by</div>
                <div className="font-medium">{selectedPost.postedByEmail}</div>

                <div className="text-slate-400">Date posted</div>
                <div>{formatDate(selectedPost.createdAt)}</div>

                {selectedPost.contact && (
                  <>
                    <div className="text-slate-400">Additional contact</div>
                    <div>{selectedPost.contact}</div>
                  </>
                )}
              </div>

              <div className="mt-6 flex flex-col sm:flex-row gap-3">
                <button
                  onClick={() => contactPoster(selectedPost)}
                  disabled={selectedPost.isSold}
                  className="flex-1 py-2.5 rounded-lg bg-emerald-600 hover:bg-emerald-500 disabled:bg-slate-700 disabled:text-slate-400 font-medium text-sm transition"
                >
                  {selectedPost.isSold ? 'This item is marked sold' : 'Contact poster (email)'}
                </button>

                {canManage(selectedPost) && (
                  <>
                    <button
                      onClick={() => toggleSold(selectedPost)}
                      className="flex-1 sm:flex-none px-5 py-2.5 rounded-lg border border-slate-600 hover:bg-slate-800 text-sm"
                    >
                      {selectedPost.isSold ? 'Mark as available' : 'Mark as sold'}
                    </button>
                    <button
                      onClick={() => handleDelete(selectedPost)}
                      className="flex-1 sm:flex-none px-5 py-2.5 rounded-lg border border-red-800 text-red-400 hover:bg-red-950/60 text-sm"
                    >
                      Delete
                    </button>
                  </>
                )}
              </div>

              <div className="mt-4 text-[11px] text-center text-slate-500">
                Transactions are between owners. Use good judgment and meet in a safe public place.
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
